import { Button, Dropdown, Tag } from "pepsico-ds";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useProductGroups } from "../../hooks/useProductGroups";
import { useProducts } from "../../hooks/useProducts";

const STEPS = {
  SELECT_TYPE: "SELECT_TYPE",
  EXCLUDE_PRODUCT_GROUPS: "EXCLUDE_PRODUCT_GROUPS",
  EXCLUDE_INDIVIDUAL_PRODUCTS: "EXCLUDE_INDIVIDUAL_PRODUCTS",
};

const EXCLUSION_TYPES = {
  PRODUCT_GROUPS: "PRODUCT_GROUPS",
  INDIVIDUAL_PRODUCTS: "INDIVIDUAL_PRODUCTS",
};

// ----- Type Selection Card -----
function ExclusionTypeCard({ title, description, selected, onClick }) {
  return (
    <div
      className={`exclusion-type-card ${selected ? "selected" : ""}`}
      onClick={onClick}
    >
      <div className="exclusion-type-title">{title}</div>
      <div className="exclusion-type-desc">{description}</div>
    </div>
  );
}

// ----- Product Group Exclusion Form -----
function ProductGroupExclusionForm({
  productGroups,
  allProducts,
  selectedProductGroups,
  setSelectedProductGroups,
  exceptionSkus,
  setExceptionSkus,
}) {
  const productGroupOptions = useMemo(
    () =>
      productGroups.map((pg) => ({
        id: pg.id,
        displayText: pg.displayText,
      })),
    [productGroups]
  );

  // Get products that belong to selected product groups (for exception selection)
  const productsInSelectedGroups = useMemo(() => {
    if (selectedProductGroups.length === 0) return [];
    const groupIds = selectedProductGroups.map((pg) => pg.id);
    return allProducts
      .filter((p) =>
        (p.keywordsGroupIds || []).some((gId) => groupIds.includes(gId))
      )
      .map((p) => ({
        id: p.metadata?.ean_upc || p.id,
        displayText: `${p.name}${p.metadata?.ean_upc ? ` (${p.metadata.ean_upc})` : ""}`,
      }));
  }, [allProducts, selectedProductGroups]);

  const selectedExceptionItems = useMemo(
    () =>
      exceptionSkus.map((sku) => {
        const product = productsInSelectedGroups.find((p) => p.id === sku);
        return product || { id: sku, displayText: sku };
      }),
    [exceptionSkus, productsInSelectedGroups]
  );

  return (
    <div className="exclusion-form">
      <div className="exclusion-form-field">
        <div className="field-label">
          <span>Product groups</span>
        </div>
        <Dropdown
          childList={productGroupOptions}
          selection="multiple"
          selectedValue={selectedProductGroups}
          setSelectedValue={(val) => setSelectedProductGroups(val || [])}
          placeholder="Select product groups"
          size="medium"
        />
      </div>

      {selectedProductGroups.length > 0 && (
        <div className="exclusion-form-field">
          <div className="field-label">
            <span>Included product exceptions</span>
          </div>
          <Dropdown
            childList={productsInSelectedGroups}
            selection="multiple"
            selectedValue={selectedExceptionItems}
            setSelectedValue={(val) =>
              setExceptionSkus((val || []).map((v) => v.id))
            }
            placeholder="Search products..."
            size="medium"
          />
          <div className="field-hint">
            <Icon icon="info" /> Exception products contribute to the metric even if
            their group is excluded
          </div>
        </div>
      )}
    </div>
  );
}

// Simple info icon replacement since we may not have the Icon import in this sub-component
function Icon({ icon }) {
  if (icon === "info") {
    return <span className="info-icon-inline">i</span>;
  }
  return null;
}

// ----- Individual Product Exclusion Form -----
function IndividualProductExclusionForm({
  allProducts,
  selectedSkus,
  setSelectedSkus,
}) {
  const productOptions = useMemo(
    () =>
      allProducts.map((p) => ({
        id: p.metadata?.ean_upc || p.id,
        displayText: `${p.name}${p.metadata?.ean_upc ? ` (${p.metadata.ean_upc})` : ""}`,
      })),
    [allProducts]
  );

  const selectedItems = useMemo(
    () =>
      selectedSkus.map((sku) => {
        const product = productOptions.find((p) => p.id === sku);
        return product || { id: sku, displayText: sku };
      }),
    [selectedSkus, productOptions]
  );

  return (
    <div className="exclusion-form">
      <div className="exclusion-form-field">
        <div className="field-label">
          <span>Products</span>
        </div>
        <Dropdown
          childList={productOptions}
          selection="multiple"
          selectedValue={selectedItems}
          setSelectedValue={(val) => setSelectedSkus((val || []).map((v) => v.id))}
          placeholder="Search products..."
          size="medium"
        />
        {selectedSkus.length > 0 && (
          <div className="selected-tags">
            {selectedSkus.map((sku) => {
              const product = productOptions.find((p) => p.id === sku);
              return (
                <Tag
                  key={sku}
                  text={product?.displayText || sku}
                  isRemovable
                  onRemove={() =>
                    setSelectedSkus((prev) => prev.filter((s) => s !== sku))
                  }
                  size="small"
                  color="blue"
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ----- Main Modal -----
export default function ProductExclusionModal({ tierRules, onClose, onApply }) {
  const { productGroups } = useProductGroups();
  const { allProducts: allProductsRaw } = useProducts();

  const allProducts = useMemo(
    () => allProductsRaw.filter((p) => !p.deleted),
    [allProductsRaw]
  );

  const [step, setStep] = useState(STEPS.SELECT_TYPE);
  const [exclusionType, setExclusionType] = useState(null);

  // Product group exclusion state
  const [selectedProductGroups, setSelectedProductGroups] = useState([]);
  const [exceptionSkus, setExceptionSkus] = useState([]);

  // Individual product exclusion state
  const [selectedSkus, setSelectedSkus] = useState([]);

  // Initialize from existing tier rules — always start on type selection step
  useEffect(() => {
    if (tierRules) {
      const existingExcludedGroups = tierRules.excludedProductGroups || [];
      const existingExcludedSkus = tierRules.excludedSkus || [];

      if (existingExcludedGroups.length > 0) {
        setExclusionType(EXCLUSION_TYPES.PRODUCT_GROUPS);
        // Pre-populate group data but stay on type selection step
        const groupItems = existingExcludedGroups.map((gId) => {
          const group = productGroups.find((pg) => pg.id === gId);
          return group || { id: gId, displayText: gId };
        });
        setSelectedProductGroups(groupItems);
        const pgExceptions = tierRules.productGroupExceptionSkus;
        if (pgExceptions) {
          const exceptionsArr = Array.isArray(pgExceptions)
            ? pgExceptions
            : [pgExceptions];
          const allSkuIds = exceptionsArr.flatMap((item) => item.skuIds || []);
          setExceptionSkus(allSkuIds);
        }
      } else if (existingExcludedSkus.length > 0) {
        setExclusionType(EXCLUSION_TYPES.INDIVIDUAL_PRODUCTS);
        setSelectedSkus(existingExcludedSkus);
      }
    }
  }, [tierRules, productGroups]);

  const handleSelectType = useCallback((type) => {
    setExclusionType(type);
    // Clear previous selections when switching type
    if (type === EXCLUSION_TYPES.PRODUCT_GROUPS) {
      setSelectedSkus([]);
    } else if (type === EXCLUSION_TYPES.INDIVIDUAL_PRODUCTS) {
      setSelectedProductGroups([]);
      setExceptionSkus([]);
    }
  }, []);

  const handleNext = () => {
    if (exclusionType === EXCLUSION_TYPES.PRODUCT_GROUPS) {
      setStep(STEPS.EXCLUDE_PRODUCT_GROUPS);
    } else if (exclusionType === EXCLUSION_TYPES.INDIVIDUAL_PRODUCTS) {
      setStep(STEPS.EXCLUDE_INDIVIDUAL_PRODUCTS);
    }
  };

  const handleApply = () => {
    if (step === STEPS.SELECT_TYPE) {
      handleNext();
      return;
    }

    const updates = {};

    if (exclusionType === EXCLUSION_TYPES.PRODUCT_GROUPS) {
      updates.excludedProductGroups = selectedProductGroups.map((pg) => pg.id);
      updates.excludedSkus = [];
      // Build productGroupExceptionSkus as an array
      if (exceptionSkus.length > 0 && selectedProductGroups.length > 0) {
        // Group exception SKUs by product group
        updates.productGroupExceptionSkus = selectedProductGroups.map((pg) => ({
          productGroupId: pg.id,
          skuIds: exceptionSkus,
        }));
      } else {
        updates.productGroupExceptionSkus = [];
      }
    } else if (exclusionType === EXCLUSION_TYPES.INDIVIDUAL_PRODUCTS) {
      updates.excludedSkus = selectedSkus;
      updates.excludedProductGroups = [];
      updates.productGroupExceptionSkus = [];
    }

    onApply(updates);
  };

  const isApplyDisabled = () => {
    if (step === STEPS.SELECT_TYPE) return !exclusionType;
    return false;
  };

  const getTitle = () => {
    if (step === STEPS.SELECT_TYPE)
      return "Products excluded from tier qualification";
    if (step === STEPS.EXCLUDE_PRODUCT_GROUPS)
      return "Products excluded from tier qualification";
    return "Products excluded from tier qualification";
  };

  return (
    <div className="product-exclusion-overlay" onClick={onClose}>
      <div className="product-exclusion-modal" onClick={(e) => e.stopPropagation()}>
        <div className="product-exclusion-header">
          <h3>{getTitle()}</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            &#10005;
          </button>
        </div>

        <div className="product-exclusion-body">
          {step === STEPS.SELECT_TYPE && (
            <div className="exclusion-type-selection">
              <ExclusionTypeCard
                title="Exclude product groups"
                description="Exclude all products belonging to selected product groups from tier evaluation."
                selected={exclusionType === EXCLUSION_TYPES.PRODUCT_GROUPS}
                onClick={() => handleSelectType(EXCLUSION_TYPES.PRODUCT_GROUPS)}
              />
              <ExclusionTypeCard
                title="Exclude individual products"
                description="Exclude only the selected SKUs from tier evaluation."
                selected={exclusionType === EXCLUSION_TYPES.INDIVIDUAL_PRODUCTS}
                onClick={() => handleSelectType(EXCLUSION_TYPES.INDIVIDUAL_PRODUCTS)}
              />
            </div>
          )}

          {step === STEPS.EXCLUDE_PRODUCT_GROUPS && (
            <ProductGroupExclusionForm
              productGroups={productGroups}
              allProducts={allProducts}
              selectedProductGroups={selectedProductGroups}
              setSelectedProductGroups={setSelectedProductGroups}
              exceptionSkus={exceptionSkus}
              setExceptionSkus={setExceptionSkus}
            />
          )}

          {step === STEPS.EXCLUDE_INDIVIDUAL_PRODUCTS && (
            <IndividualProductExclusionForm
              allProducts={allProducts}
              selectedSkus={selectedSkus}
              setSelectedSkus={setSelectedSkus}
            />
          )}
        </div>

        <div className="product-exclusion-footer">
          <Button size="medium" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={handleApply}
            disabled={isApplyDisabled()}
          >
            {step === STEPS.SELECT_TYPE ? "Select" : "Apply"}
          </Button>
        </div>
      </div>
    </div>
  );
}
